import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Eye } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

type Session = {
  id: string;
  event_name: string | null;
  status: string;
  mode: string;
  total_turns: number;
  total_cost_usd: number;
  created_at: string;
  ended_at: string | null;
  ephemeral: boolean;
};

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  completed: "secondary",
  ended: "secondary",
  abandoned: "outline",
  error: "destructive",
  listening: "default",
  speaking: "default",
  thinking: "default",
};

const PAGE_SIZE = 20;

export default function JarvisHistory() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<"7" | "30" | "90">("30");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [modeFilter, setModeFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => { setPage(0); }, [period, statusFilter, modeFilter]);

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      setLoading(true);
      const { data: mem } = await supabase
        .from("org_members")
        .select("org_id")
        .eq("user_id", user.id)
        .limit(1)
        .single();
      if (!mem?.org_id) { setLoading(false); return; }

      const since = new Date();
      since.setDate(since.getDate() - parseInt(period, 10));

      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let q = supabase
        .from("jarvis_sessions")
        .select("id, event_name, status, mode, total_turns, total_cost_usd, created_at, ended_at, ephemeral", { count: "exact" })
        .eq("org_id", mem.org_id)
        .gte("created_at", since.toISOString())
        .order("created_at", { ascending: false })
        .range(from, to);
      if (statusFilter !== "all") q = q.eq("status", statusFilter);
      if (modeFilter !== "all") q = q.eq("mode", modeFilter);
      const { data, count } = await q;
      setSessions((data ?? []) as Session[]);
      setTotal(count ?? 0);
      setLoading(false);
    })();
  }, [user?.id, period, statusFilter, modeFilter, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-headline">Histórico de sessões</h1>
          <p className="text-muted-foreground mt-1">Revise sessões anteriores e gere artefatos.</p>
        </div>
        <Button asChild>
          <Link to="/jarvis/new"><Plus className="w-4 h-4 mr-2" /> Nova sessão</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select value={period} onValueChange={(v) => setPeriod(v as "7" | "30" | "90")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="listening">Ativas</SelectItem>
                <SelectItem value="completed">Concluídas</SelectItem>
                <SelectItem value="abandoned">Abandonadas</SelectItem>
                <SelectItem value="error">Com erro</SelectItem>
              </SelectContent>
            </Select>
            <Select value={modeFilter} onValueChange={setModeFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os modos</SelectItem>
                <SelectItem value="passive">Passivo</SelectItem>
                <SelectItem value="invocation">Invocação</SelectItem>
                <SelectItem value="proactive">Proativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              Nenhuma sessão encontrada no período.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Evento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Modo</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Turnos</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Custo (USD)</TableHead>
                    <TableHead className="hidden sm:table-cell">Quando</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">
                        {s.event_name || <span className="text-muted-foreground italic">sem nome</span>}
                        {s.ephemeral && <Badge variant="outline" className="ml-2 text-[10px]">efêmera</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANTS[s.status] ?? "outline"} className="capitalize">{s.status}</Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell capitalize text-sm">{s.mode}</TableCell>
                      <TableCell className="hidden md:table-cell text-right tabular-nums">{s.total_turns}</TableCell>
                      <TableCell className="hidden md:table-cell text-right tabular-nums">${Number(s.total_cost_usd).toFixed(4)}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(s.created_at), { addSuffix: true, locale: ptBR })}
                      </TableCell>
                      <TableCell className="text-right">
                        {["listening", "thinking", "speaking", "paused", "interrupted", "warming_up", "created", "degraded"].includes(s.status) ? (
                          <Button asChild size="sm" variant="default">
                            <Link to={`/jarvis/session/${s.id}`}>
                              <Eye className="w-4 h-4 mr-1" /> Ao vivo
                            </Link>
                          </Button>
                        ) : (
                          <Button asChild size="sm" variant="ghost">
                            <Link to={`/jarvis/session/${s.id}/review`}>
                              <Eye className="w-4 h-4 mr-1" /> Review
                            </Link>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {!loading && total > 0 && (
        <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Página {page + 1} de {totalPages} · {total} sessões
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Anterior
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
